/* eslint-disable react/no-danger, react/no-unescaped-entities, max-len, react/prefer-stateless-function */
import React from 'react';
import PropTypes from 'prop-types';
import track from 'react-tracking';

import {
  Button, Spinner, Hyperlink,
} from '@edx/paragon';
import { Check } from '@edx/paragon/icons';
import { FormattedMessage } from 'gatsby-plugin-react-intl';
import { USER_SUBSCRIPTION_STATES } from '@utils/constants';
import { withLocation } from '@utils/navigation';
import withUser from '@utils/withUser';
import { submitEvent } from '@utils/analytics';
import { formatProgramPrice } from '@utils/formatting';
import { useSubscriptionButton } from '../../../../source-utils/useSubscriptionButton';
import { getLmsLink } from '../../../../utils/edxLinks';

function ProgramSubscriptionSection(props) {
  const {
    userState, enrollButtonId, isSubscriptionLoading, uuid, subscriptionDetails, courses,
  } = props;

  const { priceDetails } = subscriptionDetails;
  const price = formatProgramPrice(priceDetails?.price);

  const {
    linkRef, id, hasId, linkUrl,
  } = useSubscriptionButton(props);

  const programDetailPageURL = getLmsLink(`/dashboard/programs/${uuid}`);
  const seeAllEdxURL = process.env.GATSBY_EDX_PROGRAMS_SUBSCRIPTION_URL;

  const handleClickEvent = () => {
    const { tracking } = props;
    const isTrialEligible = !(userState === USER_SUBSCRIPTION_STATES.ACTIVE || userState === USER_SUBSCRIPTION_STATES.IN_ACTIVE);
    const isNewSubscription = !(userState === USER_SUBSCRIPTION_STATES.ACTIVE || userState === USER_SUBSCRIPTION_STATES.IN_ACTIVE);

    const getEventName = () => {
      let eventName;
      if (userState === USER_SUBSCRIPTION_STATES.ACTIVE) {
        eventName = 'edx.bi.user.subscription.program.click';
      } else if (userState === USER_SUBSCRIPTION_STATES.IN_ACTIVE) {
        eventName = 'edx.bi.user.subscription.program.restart.click';
      } else {
        eventName = 'edx.bi.user.subscription.program.start-trial.click';
      }
      return eventName;
    };

    const componentProps = {
      program_uuid: uuid,
      isTrialEligible,
      isNewSubscription,
      fromModal: false,
    };

    submitEvent(tracking, componentProps, getEventName());
  };

  const getSubscriptionSectionButtonVariants = () => {
    if (isSubscriptionLoading) {
      return (
        <Button data-testid="cta-section-loader-button" variant="brand my-3">
          <Spinner animation="border" className="subscription-spinner" />
        </Button>
      );
    }
    if (userState === USER_SUBSCRIPTION_STATES.ACTIVE) {
      return (
        <>
          <FormattedMessage
            id="prospectus.program.main.subscription.banner"
            defaultMessage="You're subscribed to this program!"
            description="Subscription Heading"
          >{text => <div className="small my-3 align-self-center text-dark">{text}</div>}
          </FormattedMessage>
          <a
            href={programDetailPageURL}
            className="enroll-btn btn btn-brand w-100 mb-3"
            {...(hasId && { id })}
            ref={linkRef}
            onClick={handleClickEvent}
          >
            <FormattedMessage
              id="prospectus.program.main.subscription.banner.details.button"
              defaultMessage="View program details"
              description="View program Details button"
            >{text => <div id={enrollButtonId}>{text}</div>}
            </FormattedMessage>
          </a>
        </>
      );
    } if (userState === USER_SUBSCRIPTION_STATES.IN_ACTIVE) {
      return (
        <a
          href={linkUrl}
          className="enroll-btn btn btn-brand w-100 mb-3 mt-5"
          {...(hasId && { id })}
          ref={linkRef}
          onClick={handleClickEvent}
        >
          <FormattedMessage
            id="prospectus.program.main.subscription.banner.resubscribe.button"
            defaultMessage="Restart my subscription"
            description="Restart my subscription button"
          >{text => <div id={enrollButtonId}>{text}</div>}
          </FormattedMessage>
        </a>
      );
    }

    return (
      <a
        data-testid="cta-enroll-now-button"
        href={linkUrl}
        className="enroll-btn btn btn-brand w-100 mb-3 mt-5"
        {...(hasId && { id })}
        ref={linkRef}
        onClick={handleClickEvent}
      >
        <FormattedMessage
          id="prospectus.program.main.button-trial"
          defaultMessage="Enroll now"
          description="Trial version button"
        >{text => <div id={enrollButtonId}>{text}</div>}
        </FormattedMessage>
      </a>
    );
  };

  return (
    <div className="mainContainer col-md-8 offset-md-2">
      <FormattedMessage
        id="prospectus.program.main.heading.subscription"
        defaultMessage="Grow your career. Start your program subscription today."
        description="Heading for the program bundle subscription section."
      >{text => <h1 className="h1 section-title text-center mb-3">{text}</h1>}
      </FormattedMessage>

      <FormattedMessage
        id="prospectus.program.main.heading.package.price"
        // eslint-disable-next-line no-template-curly-in-string
        defaultMessage="${price}/month USD"
        description="Heading for the subscription charges"
        values={{ price }}
      >{text => <h4 className="h3 mb-3 text-center text-gray-700">{text}</h4>}
      </FormattedMessage>

      <FormattedMessage
        id="prospectus.program.main.heading.package"
        defaultMessage="After a 7-day full access free trial. Cancel at any time."
        description="Heading for the subscription charges"
      >{text => <div className="lead mb-3">{text}</div>}
      </FormattedMessage>

      <FormattedMessage
        id="prospectus.program.main.heading.bullet.header"
        defaultMessage="This program subscription includes:"
        description="Heading for the bullet points"
      >{text => <div className="mb-2">{text}</div>}
      </FormattedMessage>
      <ul className="subscription-bullets">

        <li>
          <Check className="svg-item" />
          <FormattedMessage
            id="prospectus.program.main.heading.bullet.point.one"
            defaultMessage="Immediate access to all {numberOfCourses} courses in this program"
            description="Bullet point for first subscription advantages"
            values={{ numberOfCourses: courses?.length }}
          >{text => <div className="mb-2 ml-2">{text}</div>}
          </FormattedMessage>
        </li>
        <li>
          <Check className="svg-item" />
          <FormattedMessage
            id="prospectus.program.main.heading.bullet.point.three"
            defaultMessage="Course videos, lectures, and readings"
            description="Bullet point for third subscription advantages"
          >{text => <div className="mb-2 ml-2">{text}</div>}
          </FormattedMessage>
        </li>
        <li>
          <Check className="svg-item" />
          <FormattedMessage
            id="prospectus.program.main.heading.bullet.point.four"
            defaultMessage="Practice problems and assessments"
            description="Bullet point for forth subscription advantages"
          >{text => <div className="mb-2 ml-2">{text}</div>}
          </FormattedMessage>
        </li>
        <li>
          <Check className="svg-item" />
          <FormattedMessage
            id="prospectus.program.main.heading.bullet.point.two"
            defaultMessage="Graded assignments and exams"
            description="Bullet point for second subscription advantages"
          >{text => <div className="mb-2 ml-2">{text}</div>}
          </FormattedMessage>
        </li>
        <li>
          <Check className="svg-item" />
          <FormattedMessage
            id="prospectus.program.main.heading.bullet.point.five"
            defaultMessage="edX learner support"
            description="Bullet point for fifth subscription advantages"
          >{text => <div className="mb-2 ml-2">{text}</div>}
          </FormattedMessage>
        </li>
        <li>
          <Check className="svg-item" />
          <FormattedMessage
            id="prospectus.program.main.heading.bullet.point.one"
            defaultMessage="Shareable verified certificates after successfully completing a course or program"
            description="Bullet point for first subscription advantages"
          >{text => <div className="mb-2 ml-2">{text}</div>}
          </FormattedMessage>
        </li>
      </ul>

      <div className="program-pursue-btn m-auto">
        {getSubscriptionSectionButtonVariants()}
        <span className="edx-subscription-link">
          <Hyperlink className="edx-subscription-link-text" target="_blank" destination={seeAllEdxURL}>
            <FormattedMessage
              id="prospectus.program.main.call.action.open.link"
              defaultMessage="Learn more"
              description="Learn more about subs link"
            >{text => <span className="small">{text}</span>}
            </FormattedMessage>
          </Hyperlink>
          <FormattedMessage
            id="prospectus.program.main.call.action.open.link.text"
            defaultMessage="about program subscriptions."
            description="Complete text for subs"
          >{text => <div className="x-small">{text}</div>}
          </FormattedMessage>
        </span>
      </div>

    </div>
  );
}

ProgramSubscriptionSection.propTypes = {
  userState: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  courses: PropTypes.array.isRequired,
  uuid: PropTypes.string.isRequired,
  isSubscriptionLoading: PropTypes.bool,
  tracking: PropTypes.shape(),
  subscriptionDetails: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    priceDetails: PropTypes.object,
  }),
  enrollButtonId: PropTypes.string.isRequired,
};

ProgramSubscriptionSection.defaultProps = {
  isSubscriptionLoading: false,
  tracking: {},
  subscriptionDetails: {
    priceDetails: {},
  },
};

export default track({
  component: 'experiment-program-subscription-section',
})(withUser(withLocation(ProgramSubscriptionSection)));
